import * as React from 'react';
import * as i18next from 'i18next';
import { CashierPaymentsMethod, PaymentDetails, PayButton } from 'cashier-payments-method-component';
import Loader from 'wix-style-react/dist/src/Loader';
import * as styles from './CashierPaymentsWidget.scss';
import { getParameterByName } from '../../utils/locationUtils';
import PaymentMethodList from '../PaymentMethodList/PaymentMethodList';
import { IInternalCashierPaymentsProps } from '../../cashier-payments';
import { ModuleRegistry } from 'react-module-container';
import { ICashierPaymentsApi, IOflineText, IChargeResponse } from '../../cashier-payments';
import CollapsedPayment from './../CollapsedPayment/CollapsedPayment';
import { init } from '../../utils/i18n';
import biLogger from './../../utils/biLogger';
import { focusNextEl, focusPrevEl } from '../../utils/domUtils';
import { translate } from '../../Translate/Translate';
import TermsAndSubmit from './../TermsAndSubmit/TermsAndSubmit';
import cashierPayments from 'cashier-payments-sdk/dist/src';
import Logger from './../../utils/logger';
import VisibilityWrapper from './../VisibilityWrapper';
import Experiments from 'wix-experiments';

const SUPPORTED_LANGUAGES = [
  'ar', 'bg', 'cs', 'da', 'de', 'el', 'en',
  'es', 'fi', 'fr', 'he', 'hi', 'hu', 'id',
  'it', 'ja', 'ko', 'ms', 'nl', 'no', 'pl',
  'pt', 'ro', 'ru', 'sv', 'th', 'tl', 'tr',
  'zh'
];

const resources = {};
SUPPORTED_LANGUAGES.forEach((lng) => {
  resources[lng] = {
    translation: require(`./../../assets/locale/messages_${lng}.json`)
  };
});

interface ICashierPaymentsWidgetState {
  currentStep: string;
  countryCode?: string;
  error?: boolean;
  isLoading?: boolean;
  isCreditCardIframeError?: boolean;
  isPaymentSuccess?: boolean;
  isPaymentInProgress?: boolean;
  methods?: { value: string, label: string, icon?: string, additionalInfo?: any, connectedProviderId?: string, mandatoryFields?: any }[];
  offlineText?: IOflineText;
  selectedMethod?: string;
  isMethodCollapsed?: boolean;
  collapsedMessage?: string;
  collapsedIcon?: string;
  isTermsAccepted?: boolean;
  generalErrorMessage?: string;
  isCreditCardIframeLoading?: boolean;
  mandatoryFieldExperimentEnabled?: boolean;
  mandatoryFieldForCheckoutEnabled?: boolean;
  mandatoryFields?: any;
}

export default class CashierPaymentsWidget extends React.Component<IInternalCashierPaymentsProps, ICashierPaymentsWidgetState> implements ICashierPaymentsApi {
  private logger: {
    appLoaded: () => any;
    interactionStarted: (name: string) => any;
    interactionEnded: (name: string) => any;
  };
  private buyerLoadStartTime: number;
  private creditCardLoadDate: number;
  private settingsLoadTimer;
  private _isComponentMounted: boolean = true; // TODO this is trick to resolve warning with fetchSettings request, we need to improve sdk
  private fetchSettingsPromise: any;
  private paymentDetailsInstance: any; // Payment details instance for validating mandatory fields

  static defaultProps = {
    walletPaymentInNewTab: true,
    externalSubmitButton: false,
    termsAndConditionsLink: '',
    useTermsAndConditions: false,
    theme: 'accordion',
    showOnlyCurrentStep: false
  };

  constructor(props: IInternalCashierPaymentsProps) {
    super(props);
    const i18n = init(this.getLocale(), resources, () => ({}));
    this.logger = props.logger || new Logger();

    this.state = {
      currentStep: 'paymentMethod',
      isLoading: true,
      methods: [],
      isPaymentSuccess: false,
      isTermsAccepted: false,
      isPaymentInProgress: false,
      selectedMethod: '',
      isCreditCardIframeLoading: false,
      mandatoryFieldExperimentEnabled: false,
      mandatoryFieldForCheckoutEnabled: false,
      mandatoryFields: {},
    };

    biLogger.init({
      appId: props.configuration.appId,
      appInstanceId: props.configuration.appInstanceId,
      visitorId: props.configuration.visitorId,
      orderSnapshotId: props.configuration.snapshotId,
      msid: props.configuration.msid,
    });

    biLogger.buyerLoadStart({duration: 0});
    this.buyerLoadStartTime = Date.now();

    if (props.initApi) {
      props.initApi(this);
    }

    const href = window.location.href;
    cashierPayments.init({
      type: 'RAW',
      noPrefetchOnInit: true,
      host: props.host,
      walletExpressCheckoutAPIUrl: props.configuration.walletExpressCheckoutAPIUrl,
      walletPaymentInNewTab: props.walletPaymentInNewTab,
      appId: this.props.configuration && this.props.configuration.appId,
      appInstanceId: this.props.configuration && this.props.configuration.appInstanceId,
      visitorId: this.props.configuration && this.props.configuration.visitorId,
      msid: this.props.configuration && this.props.configuration.msid,
      snapshotId: this.props.configuration && this.props.configuration.snapshotId,
      query: {
        providerId: getParameterByName('providerId', href),
        submitType: getParameterByName('submitType', href) || 'external',
        creditCardMinInstallments: getParameterByName('creditCardMinInstallments', href),
        creditCardMaxInstallments: getParameterByName('creditCardMaxInstallments', href),
        isInstallmentsSupported: getParameterByName('isInstallmentsSupported', href),
        country: (this.props.configuration && this.props.configuration.country) || getParameterByName('country', href),
        amount: props.configuration.amount,
        orderId: props.configuration.snapshotId,
        locale: this.getLocale(),
      },
      onError: (method, data) => {
        if (props.onError) {
          props.onError(method, data);
        }
        this.setState({
          isPaymentInProgress: false
        });
      },
      creditCardFormUrl: props.configuration.cashierPCIUrl,
      onMessage: this.handleMessageFromSdk,
      onPaymentMethodChanged: (method) => {
        const metadata = cashierPayments.getPaymentMethodMetadata(method);
        props.paymentMethodChanged(method, metadata);
        this.changeActivePaymentMethod(method);
      },
      onPaymentStart: (method) => {
        if (method !== 'offline' && method !== 'creditCard' && !props.walletPaymentInNewTab) {
          // because we will lose all our js, so we log start and end in same time
          this.logger.interactionStarted(`pay-with-${method}`);
          this.logger.interactionEnded(`pay-with-${method}`);
        } else {
          this.logger.interactionStarted(`pay-with-${method}`);
        }
        const methodObj = this.state.methods.find(it => it.value === method);
        biLogger.submitOrder({
          errorDesc: null,
          paymentMethodType: method,
          paymentProvider: methodObj && methodObj.connectedProviderId,
          status: 1
        });
        if (props.onPaymentStart) {
          props.onPaymentStart(method);
        }
        if (!this.state.isPaymentInProgress) {
          this.setState({
            isPaymentInProgress: true
          });
        }
      },
      onPaymentComplete: (method: string, result: any) => {
        this.logger.interactionEnded(`pay-with-${method}`);
        if (method !== 'offline' && method !== 'creditCard') {
          if (props.onWalletPaymentComplete) {
            props.onWalletPaymentComplete(result);
          }
        }
        if (result && result.type === 'SERVER_FORM_SUBMIT') {
          const cc = this.state.methods.find(it => it.value === 'creditCard');
          biLogger.submitOrder({
            errorDesc: result.status === 'FAILURE' ? result.message : null,
            paymentMethodType: method,
            paymentProvider: cc && cc.connectedProviderId,
            status: 1
          });
          biLogger.transactionStatus({
            paymentMethodType: method,
            paymentProvider: cc && cc.connectedProviderId,
            transactionId: '',
            transactionStatus: result.status === 'FAILURE' ? 'Failure' : 'Success',
          });
        }
        if (result && result.type === 'CLIENT_FORM_SUBMIT') {
          const cc = this.state.methods.find(it => it.value === 'creditCard');
          biLogger.submitOrder({
            errorDesc: result.status === 'FAILURE' ? result.message : null,
            paymentMethodType: method,
            paymentProvider: cc && cc.connectedProviderId,
            status: Boolean(result.status)
          });
        }
        const pendingMethods = ['PENDING_MERCHANT', 'PENDING_BUYER', 'INITIALIZED', 'OFFLINE'];
        const successMethods = ['APPROVED', 'OK'];
        const okMethods = [...successMethods, ...pendingMethods];
        const doneMethods = [...okMethods];
        const publicResult = {
          ...result,
          status: doneMethods.indexOf(result.status) !== -1 ? 'OK' : result.status,
          isPending: pendingMethods.indexOf(result.status) !== -1,
        };

        if (props.paymentResult) {
          props.paymentResult({ ...publicResult, method });
          this.handleSubmitResult(result);
        }

        if (result && okMethods.indexOf(result.status) > -1) {
          this.setState({
            isPaymentSuccess: true
          });
        }
        if (this.state.isPaymentInProgress) {
          this.setState({
            isPaymentInProgress: false
          });
        }

        if (props.onPaymentComplete) {
          props.onPaymentComplete(method, publicResult);
        }
      }
    });
  }

  componentDidMount() {
    this.logger.appLoaded();
    this.settingsLoadTimer = setTimeout(() => {
      this.setState({
        generalErrorMessage: 'Sorry, but something went wrong!'
      });
    }, 8000);
    this.fetchSettingsPromise = cashierPayments.fetchSettings(this.props.configuration).then((settings) => {
      clearTimeout(this.settingsLoadTimer);
      this.setState({isLoading: false, error: false, countryCode: settings.countryCode || ''});
      if (this._isComponentMounted) {
        biLogger.buyerLoadCompleted({
          duration: Date.now() - this.buyerLoadStartTime,
          status: true
        });
        this.setPaymentsMethods(settings.paymentMethods, settings.offlineText);
      }
      return settings;
    });

    // We support mandatory fields just with our submit button
    if (this.experimentsExistAtWindow() && this.mandatoryFieldsExperimentEnabled()) {
      const state = {
        mandatoryFieldExperimentEnabled: true,
        mandatoryFieldForCheckoutEnabled: false,
      };
      if (this.mandatoryFieldsForCheckoutExperimentEnabled()) {
        state.mandatoryFieldForCheckoutEnabled = true;
      }
      this.setState(state);
    } else {
      const experiments = this.props.experimentsInstance || new Experiments({baseUrl: 'https://www.wix.com'});
      experiments.load('cashier').then(() => this.setState({
        mandatoryFieldExperimentEnabled: experiments.enabled('specs.cashier.MandatoryFields'),
        mandatoryFieldForCheckoutEnabled: experiments.enabled('specs.payments.MandatoryFields'),
      }));
    }

    // We should use `mandatoryFields` as a state to update PaymentDetails component
    // after `setMandatoryFields` method called
    if (this.props.mandatoryFields && Object.keys(this.props.mandatoryFields).length) {
      this.setState({
        mandatoryFields: this.props.mandatoryFields
      });
    }
  }

  experimentsExistAtWindow = () => {
    return typeof window !== 'undefined' && !!window['__EXPERIMENTS__'];
  }

  mandatoryFieldsExperimentEnabled = () => {
    return window['__EXPERIMENTS__']['specs.cashier.MandatoryFields'] === 'true';
  }

  mandatoryFieldsForCheckoutExperimentEnabled = () => {
    return window['__EXPERIMENTS__']['specs.payments.MandatoryFields'] === 'true';
  }

  fetchPaymentMethods = (country?: string): Promise<any> => {
    return cashierPayments.fetchPaymentMethodsByConfiguration(this.props.configuration, country);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.configuration.country && prevState.countryCode && prevState.countryCode && prevState.countryCode !== this.state.countryCode) {
      this.updateCountry(this.state.countryCode);
    }
  }

  componentWillReceiveProps(nextProps, nextState) {
    if (this.props.configuration.country !== nextProps.configuration.country) {
      this.updateCountry(nextProps.configuration.country);
    }
  }

  componentWillUnmount() {
    this._isComponentMounted = false;
  }

  updateCountry = (country: string) => {
    cashierPayments.setCountry(country);
    this.setState({isLoading: true});
    this.fetchSettingsPromise = cashierPayments.fetchSettings(this.props.configuration).then((settings) => {
      this.setState({isLoading: false});
      if (this._isComponentMounted) {
        this.setPaymentsMethods(settings.paymentMethods, settings.offlineText);
      }
    });
  }

  tryParseCustomOfflineText(additionalInfo) {
    if (additionalInfo && additionalInfo.merchantInstructions) {
      let parsedText = null;
      try {
        parsedText = JSON.parse(additionalInfo.merchantInstructions);
      } catch (ex) {
        parsedText = null;
      }
      return parsedText;
    }
    return additionalInfo;
  }

  handleCountryChange = (country) => {
    this.setState({
      countryCode: country && country.value
    });
  }

  handleMessageFromSdk = (method, message) => {
    if (message.type === 'PING' && message.status === 'INITED' && this._isComponentMounted) {
      const cc = this.state.methods.find(it => it.value === 'creditCard');
      biLogger.creditCardLoaded({
        duration: Date.now() - this.creditCardLoadDate,
        paymentCategory: 'CreditCard',
        paymentProvider: cc && cc.connectedProviderId,
        source: null,
      });
    } else if (message.type === 'LOAD_STARTED' && method === 'creditCard') {
      this.creditCardLoadDate = Date.now();
    }
    if (message.type === 'CLIENT_FORM_SUBMIT') {
      if (message.status !== 'OK' && message.status !== 'CLIENT_OK' && message.status !== 'IN_PROGRESS' && this.state.isPaymentInProgress) {
        this.setState({
          isPaymentInProgress: false
        });
      }
    }
    if (this.props.paymentResult) {
      this.props.paymentResult({ ...message, method });
    }
  }

  _getAdditionalInfo = pm => {
    switch (pm.paymentMethod) {
      case 'offline':
        return this.tryParseCustomOfflineText(pm.additionalInfo);
      case 'creditCard':
        return {...pm.addtionalInfo, installments: pm.installments};
      default:
        return pm.additionalInfo;
    }
  }

  _getUniqueMandatoryFields = (paymentMethods?: any): Array<string> => {
    const methods = paymentMethods || this.state.methods;
    const allMandatoryFields = methods
      // Gets only methods with mandatory fields provided
      .filter(m => !!m.mandatoryFields)

      // Gets mandatory fields of every payment method
      .map(m => m.mandatoryFields);

    return []
      // Array flattening: [[{fieldName: 'first'}, {fieldName: 'last'}], [{fieldName: 'email'}]]
      // => [{fieldName: 'first'}, {fieldName: 'last'}, {fieldName: 'email'}]
      .concat.apply([], allMandatoryFields)

      // Mapping to fieldName and its last part after the dot: 'billing.info.address' => 'address'
      .map(m => m.fieldName.split('.').pop())

      // Get unique fields
      .filter((value, index, self) => self.indexOf(value) === index);
  }

  fetchMandatoryFields = (country?: string): Promise<Array<string>> => {
    return this.fetchPaymentMethods(country).then(paymentMethodsResult => {
      return this._getUniqueMandatoryFields(paymentMethodsResult.paymentMethods);
    });
  }

  getAllMandatoryFields = () => {
    return new Promise((resolve, reject) => {
      this.fetchSettingsPromise.then((settingsResponse) => {
        resolve(this._getUniqueMandatoryFields(settingsResponse.paymentMethods));
      }).catch(() => {
        reject();
      });
    });
  }

  setMandatoryFields = (mandatoryFields) => {
    this.setState({
      mandatoryFields
    });
  }

  setPaymentsMethods = (paymentMethods, offlineText) => {
    this.setState({
      methods: (paymentMethods || []).map(pm => {
        const additionalInfo = this._getAdditionalInfo(pm);
        let translateKey = pm.paymentMethod;
        if (pm.paymentMethod === 'offline') {
          translateKey = (offlineText && offlineText.titleId) || (additionalInfo && additionalInfo.titleId) || 'settings.offlineTitleOptionDefault';
        }
        return {
          icon: pm.icon,
          connectedProviderId: pm.connectedProviderId,
          value: pm.paymentMethod,
          additionalInfo,
          label: translate({forKey: translateKey}) || pm.paymentMethod,
          mandatoryFields: pm.mandatoryFields
        };
      }),
      offlineText
    }, () => {
      let activeMethod = this.props.configuration.defaultPaymentMethod || (this.state.methods[0] && this.state.methods[0].value);
      if (paymentMethods && paymentMethods.find(pm => pm.paymentMethod === this.state.selectedMethod)) {
        activeMethod = this.state.selectedMethod;
      }
      return cashierPayments.setActivePaymentMethod(activeMethod);
    });
  }

  handleTermsStatusChange = (e) => this.setState({ isTermsAccepted: e.target.checked });

  arrowKeysFunctions = {
    ArrowDown: focusNextEl,
    ArrowUp: focusPrevEl
  };

  handleArrowKeys = (e) => {
    if (this.arrowKeysFunctions[e.key]) {
      this.arrowKeysFunctions[e.key](e.target);
      e.preventDefault();
    }
  }

  getLocale() {
    return this.props.configuration.locale
      || this.props.locale
      || getParameterByName('locale', window.location.href)
      || 'en';
  }

  isStepVisible(step: string) {
    return this.props.showOnlyCurrentStep === false || this.state.currentStep === step;
  }

  setCurrentStep(currentStep: string) {
    this.setState({currentStep});
  }

  renderGeneralError() {
    const { generalErrorMessage } = this.state;
    if (!generalErrorMessage) {
      return null;
    }
    return (
      <div data-hook="general-error-message" role="alert" className={styles.generalError}>
        <div className={styles.generalErrorContent}>
          <img className={styles.circleIcon} src={require('./../../assets/img/error-icon.svg')} />
          <span
            className={styles.generalErrorText}
            title={generalErrorMessage}
          >
            {generalErrorMessage}
          </span>
        </div>
      </div>
    );
  }

  renderPaymentComponent() {
    if (!this.getActivePaymentMethod()) {
      return null;
    }

    return (
      <div>
        <VisibilityWrapper display={this.state.isMethodCollapsed}>
          <CollapsedPayment
            iconName={this.state.collapsedIcon}
            title={this.state.collapsedMessage || '●●●● ●●●● ●●●● ●●●●'}
          />
        </VisibilityWrapper>
        <VisibilityWrapper visible={!this.state.isMethodCollapsed}>
          <CashierPaymentsMethod
            locale={this.getLocale()}
            orderId={this.props.configuration.snapshotId}
            paymentsSDK={cashierPayments}
            paymentMethod={this.getActivePaymentMethod()}
            payButtonLabel={this.props.externalSubmitButton ? 'button.pay.placeOrder' : 'button.pay.buyNow'}
            externalPayButton={this.props.externalSubmitButton}
            isMobileApplication={false}
          />
        </VisibilityWrapper>
      </div>
    );
  }

  renderPaymentDetails() {
    // Payment details & Mandatory fields should be shown only with internal button and if active payment method exists
    if (!this.state.mandatoryFieldExperimentEnabled || !this.getActivePaymentMethod()) {
      return null;
    }

    if (this.props.externalSubmitButton && !this.state.mandatoryFieldForCheckoutEnabled) {
      return null;
    }

    return (
      <PaymentDetails
        ref={(paymentDetails) => {
          this.paymentDetailsInstance = paymentDetails;
        }}
        countryCode={this.state.countryCode}
        onCountryChange={this.handleCountryChange}
        locale={this.getLocale()}
        orderId={this.props.configuration.snapshotId}
        paymentsSDK={cashierPayments}
        paymentMethod={this.getActivePaymentMethod()}
        isMandatoryFieldsEnabled={this.state.mandatoryFieldExperimentEnabled}
        mandatoryFields={this.state.mandatoryFields}
        isMethodCollapsed={this.state.isMethodCollapsed}
      />
    );
  }

  renderSubmitArea() {
    if (this.state.isMethodCollapsed) {
      return null;
    }

    return (
      <VisibilityWrapper display={!this.props.externalSubmitButton}>
        <div>
          <VisibilityWrapper display={this.props.useTermsAndConditions}>
            <TermsAndSubmit
              termsAndConditionsLink={this.props.termsAndConditionsLink}
              handleTermsStatusChange={this.handleTermsStatusChange}
            />
          </VisibilityWrapper>
          <div style={{marginTop: 24}}>
            <PayButton
              locale={this.getLocale()}
              withMandatoryFields={this.state.mandatoryFieldExperimentEnabled}
              text={this.props.externalSubmitButton ? 'button.pay.placeOrder' : 'button.pay.buyNow'}
              orderId={this.props.configuration.snapshotId}
              paymentsSDK={cashierPayments}
              paymentMethod={this.getActivePaymentMethod()}
            />
          </div>
        </div>
      </VisibilityWrapper>
    );
  }

  renderStepNavigation() {
    if (!this.props.showOnlyCurrentStep) {
      return null;
    }

    if (this.isStepVisible('paymentDetails')) {
      return (
        <button
          data-hook="wcn-back-button"
          onClick={() => this.setCurrentStep('paymentMethod')}
        >{i18next.t('payments.steps.back')}</button>
      );
    }
  }

  renderStepTitle() {
    if (!this.props.showOnlyCurrentStep) {
      return null;
    }

    const titleKey = `payments.steps.${this.state.currentStep}.label`;
    return (titleKey ? <h2 data-hook="wcn-step-title">{i18next.t(titleKey)}</h2> : null);
  }

  renderPaymentMethodStep() {
    if (!this.isStepVisible('paymentMethod')) {
      return null;
    }

    if (this.state.isMethodCollapsed || this.state.methods.length <= 1) {
      return null;
    }

    return (
      <PaymentMethodList
        radioButtonClassName={this.props.radioButtonClassName}
        activePaymentComponentNode={this.props.theme === 'accordion' && this.renderPaymentComponent()}
        paymentMethods={this.state.methods}
        selectedMethod={this.state.selectedMethod}
        onPaymentMethodChanged={this.onPaymentMethodChanged}
      />
    );
  }

  renderPaymentDetailsStep() {
    if (!this.isStepVisible('paymentDetails')) {
      return null;
    }

    return (
      <div data-hook="wcn-step-paymentDetails">
        {this.props.theme !== 'accordion' || this.state.isMethodCollapsed || (this.props.theme === 'accordion' && this.state.methods.length === 1) ? this.renderPaymentComponent() : null}
        {this.renderPaymentDetails()}
        {this.renderSubmitArea()}
      </div>
    );
  }

  render() {
    const rtlClassName = cashierPayments.isRTLLocale(this.getLocale())
      ? 'cashier-payments-widget-rtl'
      : 'cashier-payments-widget-ltr' ;

    if (this.state.isLoading) {
      if (this.state.generalErrorMessage) {
        return this.renderGeneralError();
      }

      return (
        <div
          className={`${styles.paymentsWidget} ${rtlClassName}`}
          style={{ maxWidth: this.props.maxWidth }}
          data-hook="wcn-payment-widget"
        >
          <div data-hook="cc-spinner-main" className={styles.loaderWrap}><Loader size="medium"/></div>
        </div>
      );
    }

    return (
      <div
        className={`${styles.paymentsWidget} ${rtlClassName}`}
        style={{ maxWidth: this.props.maxWidth }}
        data-hook="wcn-payment-widget"
        onKeyDown={this.handleArrowKeys}
      >
        {!this.state.isMethodCollapsed && this.renderGeneralError()}
        {this.renderStepNavigation()}
        {this.renderStepTitle()}
        {this.renderPaymentMethodStep()}
        {this.renderPaymentDetailsStep()}
      </div>
    );
  }

  getActivePaymentMethod = () => {
    const paymentMethod: any = this.state.methods.find(method => method.value === this.state.selectedMethod);
    if (!paymentMethod) {
      return null;
    }

    return {
      paymentMethod: paymentMethod.value,
      name: paymentMethod.label,
      connectedProviderId: paymentMethod.connectedProviderId,
      additionalInfo: {
        installments: paymentMethod.installments,
        ...(paymentMethod.additionalInfo || {})
      }
    };
  }

  private onPaymentMethodChanged = (pmId: string) => {
    this.setCurrentStep('paymentDetails');
    cashierPayments.setActivePaymentMethod(pmId);
  }

  private changeActivePaymentMethod = (pmId) => {
    biLogger.buyerChoosedPaymentMethod({paymentMethod: pmId});
    this.setState({
      selectedMethod: pmId,
      isMethodCollapsed: false,
      collapsedMessage: '',
      generalErrorMessage: '',
      isCreditCardIframeLoading: false,
      isCreditCardIframeError: false
    });
  }

  private collapseCurrentMethod = () => this.setState({ isMethodCollapsed:  true });

  private expandCurrentMethod = () => this.setState({ isMethodCollapsed: false });

  private handleSubmitResult = (result) => {
    const generalErrorMessage = result.errorCode ? result.translatedError || translate({ forKey: 'payment.submitError' }) : '';
    this.setState({ generalErrorMessage });
    return true;
  }

  private handleCollapseResult = (result) => {
    this.setState({
      collapsedMessage: result.message,
      collapsedIcon: result.icon
    });
    return true;
  }

  public tokenize = (): Promise<any> => cashierPayments.tokenize(this.state.selectedMethod);

  public continuePayment = (chargeResponse: IChargeResponse, {inNewTab} = {inNewTab: this.props.walletPaymentInNewTab}): Promise<any> => {
    return cashierPayments.continuePayment(this.state.selectedMethod, {chargeResponse, inNewTab});
  }

  public launchPaymentPopup = (chargeResponse: IChargeResponse): Promise<any> => {
    return cashierPayments.launchPaymentPopup(this.state.selectedMethod, {chargeResponse});
  }

  public cancelPaymentPopup = () => {
    return cashierPayments.cancelPaymentPopup(this.state.selectedMethod);
  }

  public localizeError = (errorCode: string, locale?: string): Promise<any> => cashierPayments.localizeError(errorCode, locale || this.getLocale());

  public submit = (): Promise<any> => {
    this.setState({
      generalErrorMessage: ''
    });
    cashierPayments.submit();

    return Promise.resolve(true);
  }

  public pay = (): Promise<any> => {
    this.setState({
      generalErrorMessage: ''
    });
    return cashierPayments.pay(this.state.selectedMethod);
  }

  public validate = (): Promise<any> => {
    if (this.paymentDetailsInstance) {
      return this.paymentDetailsInstance.validate();
    }
    return cashierPayments.validate();
  }

  public expand = (): Promise<any> => {
    this.expandCurrentMethod();
    return cashierPayments.expand();
  }

  public collapse = (): Promise<any> => new Promise((resolve, reject) => {
    this.validate()
      .then((validationResult) => {
        if (validationResult.isValid) {
          cashierPayments.collapse().then(data => {
            if (this.state.selectedMethod === 'creditCard') {
              this.handleCollapseResult({...data, message: `●●●● ●●●● ●●●● ${data.lastFourDigits}`, icon: data.type});
            } else if (this.state.selectedMethod === 'offline') {
              this.handleCollapseResult({...data, message: this.renderPaymentComponent()});
            } else {
              const method: any = this.getActivePaymentMethod();
              this.handleCollapseResult({
                ...data,
                message: <span>{i18next.t('payments.collapsedMode.label',
                  {paymentMethodLabel: method.name})} </span>,
                icon: method.paymentMethod
              });
            }
            this.collapseCurrentMethod();
            resolve(data);
          }).catch(e => reject(e));
        } else {
          reject();
        }
      })
      .catch((e) => {
        reject(e);
      });
  })

  public initializePayment = (): Promise<any> => {
    return cashierPayments.initializePayment(this.state.selectedMethod);
  }
}

ModuleRegistry.registerComponent('CashierPaymentsWidget', () => CashierPaymentsWidget);



// WEBPACK FOOTER //
// ./components/CashierPaymentsWidget/CashierPaymentsWidget.tsx